import { z } from 'zod';
import { anchorsSchema, type KeyDateRef } from './key-date';
import { monthIndexSchema, type MonthIndex } from './time';

/**
 * A value that is either constant or changes step-wise over time (CLAUDE.md "Time-varying values").
 * Segments are sorted by `from`; each lasts until the next one starts; the last runs to the horizon.
 * If the first segment starts after month 0, `fallback` covers the gap. A period's start may be
 * tied to a key date (`anchors.from`).
 */
export type Scheduled<T> =
  | { kind: 'constant'; value: T }
  | {
      kind: 'schedule';
      segments: {
        from: MonthIndex;
        value: T;
        anchors?: { from?: KeyDateRef | undefined } | undefined;
      }[];
      fallback?: T | undefined;
    };

export function scheduledSchema<S extends z.ZodType>(value: S) {
  const schedule = z
    .object({
      kind: z.literal('schedule'),
      segments: z
        .array(z.object({ from: monthIndexSchema, value, anchors: anchorsSchema('from') }))
        .min(1)
        .max(240),
      fallback: value.optional(),
    })
    .superRefine((s, ctx) => {
      const first = s.segments[0];
      if (first && first.from !== 0 && s.fallback === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'The first period must start at month 0, or a fallback value is needed',
          path: ['segments', 0, 'from'],
        });
      }
      for (let i = 1; i < s.segments.length; i++) {
        const prev = s.segments[i - 1];
        const cur = s.segments[i];
        if (prev && cur && cur.from <= prev.from) {
          ctx.addIssue({
            code: 'custom',
            message: 'Periods must be sorted and must not overlap',
            path: ['segments', i, 'from'],
          });
        }
      }
    });
  return z.discriminatedUnion('kind', [z.object({ kind: z.literal('constant'), value }), schedule]);
}

export const constant = <T>(value: T): Scheduled<T> => ({ kind: 'constant', value });

export function isScheduleSegmented<T>(
  s: Scheduled<T>,
): s is Extract<Scheduled<T>, { kind: 'schedule' }> {
  return s.kind === 'schedule';
}

/**
 * Expands a schedule into a per-month lookup: `values[t]` is the value in force at month `t`,
 * `starts[t]` the month its segment began (0 for constants/fallback). Resolve once, before any
 * monthly loop, so hot paths never search segments.
 */
export function resolveScheduled<T>(
  s: Scheduled<T>,
  months: number,
): { values: T[]; starts: Int32Array } {
  const starts = new Int32Array(months);
  if (s.kind === 'constant') {
    return { values: new Array<T>(months).fill(s.value), starts };
  }
  const values = new Array<T>(months);
  let segIndex = -1;
  let current: T | undefined = s.fallback;
  let currentStart = 0;
  for (let t = 0; t < months; t++) {
    let next = s.segments[segIndex + 1];
    while (next && next.from <= t) {
      segIndex++;
      current = next.value;
      currentStart = next.from;
      next = s.segments[segIndex + 1];
    }
    if (current === undefined) {
      // Unreachable for validated input (first segment at 0 or fallback present).
      throw new Error('Schedule has no value for month ' + String(t));
    }
    values[t] = current;
    starts[t] = currentStart;
  }
  return { values, starts };
}

/** Value in force at month `t` (for previews and one-off lookups; engines use `resolveScheduled`). */
export function scheduledValueAt<T>(s: Scheduled<T>, t: number): T | undefined {
  if (s.kind === 'constant') return s.value;
  let value = s.fallback;
  for (const seg of s.segments) {
    if (seg.from > t) break;
    value = seg.value;
  }
  return value;
}
